import React, { useContext, useEffect, useMemo } from 'react';
import classNames from 'classnames';
import { useLocation } from 'umi';
import { Layout } from 'tea-component';
import componentMap from '@/pages/dashboard/_config/compConfig';
import { GAME_CATEGORY } from '@opm/components/FilterCommon/constants/games';
import { buriedPointGame, typeIdObj } from '@/pages/home/utils/buriedPoint';
import { StudioContext } from '@home/pages/studio/context';
import StudioQueryTabs from '../StudioQueryTabs';
import MarketingPageWrap from '../MarketingPageWrap';
import StudioTabPrNoticeMarquee from '../StudioTabPrNoticeMarquee';
import {
  getStudioAdCreativesFilterInstanceId,
  type IAdCreativesWrapperConfig,
  type IAdCreativesTabConfig,
  type ICompetitorAdComparisonStudioProps,
  type IZoneTopCreativesStudioProps,
} from './types';
import './index.scss';

const { Content } = Layout;

/** 同一 Tab 短时间内的重复曝光去重（Strict Mode 双 mount / 父级重挂载） */
const tabLogDedupe = { key: '', at: 0 };
const TAB_LOG_DEDUPE_MS = 800;

function reportStudioTabLog(
  tab: IAdCreativesTabConfig | undefined,
  gameName: string | undefined,
  gameId: string | undefined,
) {
  const logConfig = tab?.logConfig;
  const tabKey = tab?.key;
  if (!logConfig?.enabled || !gameId || !tabKey) {
    return;
  }
  const now = Date.now();
  if (tabLogDedupe.key === tabKey && now - tabLogDedupe.at < TAB_LOG_DEDUPE_MS) {
    return;
  }
  tabLogDedupe.key = tabKey;
  tabLogDedupe.at = now;
  buriedPointGame({
    gameName,
    pageId: logConfig.pageId || '',
    pageName: logConfig.pageName,
    extInfo: logConfig.extInfo,
    pagePath: logConfig.pagePath,
    typeId: typeIdObj.studio,
  }, gameId, 'unified_id');
}

function resolveActiveTabKey(
  tabs: IAdCreativesTabConfig[],
  queryValue: string | null,
  defaultTab?: string,
) {
  const validKeys = new Set(tabs.map((tab) => tab.key));
  if (queryValue && validKeys.has(queryValue)) {
    return queryValue;
  }
  if (defaultTab && validKeys.has(defaultTab)) {
    return defaultTab;
  }
  return tabs[0]?.key;
}

function resolveCondGameIds(
  tab: IAdCreativesTabConfig,
  wrapperCondGameIds?: string[],
) {
  const tabCondGameIds = tab.componentProps?.condGameIds;
  if (Array.isArray(tabCondGameIds)) {
    return tabCondGameIds;
  }
  return wrapperCondGameIds;
}

function renderMarketingPage(
  tab: IAdCreativesTabConfig,
  wrapperCondGameIds?: string[],
) {
  const Component = componentMap[tab.name] as React.ComponentType<any> | undefined;
  if (!Component) {
    return null;
  }

  const filterInstanceId = getStudioAdCreativesFilterInstanceId(tab.key);
  const studioLogConfig = tab.logConfig;
  const condGameIds = resolveCondGameIds(tab, wrapperCondGameIds);

  if (tab.name === 'ZoneTopCreatives') {
    const props = tab.componentProps as IZoneTopCreativesStudioProps | undefined;
    const defaultFilter = {
      ...(props?.defaultFilter || {}),
      game_category: GAME_CATEGORY.ALL,
    };

    return (
      <Component
        {...props}
        defaultFilter={defaultFilter}
        filterInstanceId={props?.filterInstanceId || filterInstanceId}
        studioLogConfig={props?.studioLogConfig || studioLogConfig}
        condGameIds={condGameIds}
      />
    );
  }

  if (tab.name === 'CompetitorAdComparison') {
    const props = tab.componentProps as ICompetitorAdComparisonStudioProps | undefined;
    return (
      <Component
        {...props}
        filterInstanceId={props?.filterInstanceId || filterInstanceId}
        studioLogConfig={props?.studioLogConfig || studioLogConfig}
        condGameIds={condGameIds}
      />
    );
  }

  return (
    <Component
      {...tab.componentProps}
      filterInstanceId={filterInstanceId}
      studioLogConfig={studioLogConfig}
      condGameIds={condGameIds}
    />
  );
}

interface IAdCreativesWrapperProps {
  config?: IAdCreativesWrapperConfig;
}

const AdCreativesWrapper: React.FC<IAdCreativesWrapperProps> = ({ config }) => {
  const location = useLocation();
  const { currentGame } = useContext(StudioContext);
  const { unified_edition_id: gameId } = currentGame || {};
  const {
    tabs = [],
    queryKey = 'tab',
    defaultTab,
    titleEn,
    titleCn,
    descriptionEn,
    descriptionCn,
    condGameIds: wrapperCondGameIds,
  } = config || {};

  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const activeKey = resolveActiveTabKey(
    tabs,
    searchParams.get(queryKey),
    defaultTab,
  );
  const activeTab = tabs.find((tab) => tab.key === activeKey) || tabs[0];
  const isMultiTab = tabs.length > 1;
  const hasPageTitle = !!(titleEn || titleCn);
  const showHeader = isMultiTab || hasPageTitle;

  useEffect(() => {
    if (!isMultiTab || !gameId || !activeTab) {
      return;
    }
    reportStudioTabLog(activeTab, currentGame?.game_name, gameId);
  }, [activeKey, gameId, isMultiTab, activeTab, currentGame?.game_name]);

  return (
    <Content className={classNames(
      'dbh-studio-ad-creatives',
      activeKey && `dbh-studio-ad-creatives--${activeKey}`,
      hasPageTitle && 'dbi-businessreports',
    )}>
      {showHeader && (
        <StudioQueryTabs
          titleEn={titleEn}
          titleCn={titleCn}
          descriptionEn={descriptionEn}
          descriptionCn={descriptionCn}
          tabs={isMultiTab ? tabs.map((tab) => ({
            key: tab.key,
            labelEn: tab.nameEn,
            labelCn: tab.nameCn,
          })) : []}
          activeKey={activeKey}
          defaultActiveKey={defaultTab}
          queryKey={queryKey}
        />
      )}
      <Content.Body full className="dbh-studio-ad-creatives-body">
        {activeTab && (
          <MarketingPageWrap key={activeTab.key}>
            <StudioTabPrNoticeMarquee
              tabKey={activeTab.key}
              prNoticeMarquee={activeTab.prNoticeMarquee}
            />
            {renderMarketingPage(activeTab, wrapperCondGameIds)}
          </MarketingPageWrap>
        )}
      </Content.Body>
    </Content>
  );
};

export default AdCreativesWrapper;
